import { create } from 'zustand';
import axios from 'axios';
import { API_URL } from '../utils/constants';
import { cloudSync } from '../utils/cloudStorage';
import axiosInstance from './api';

const api = axios.create({
  baseURL: API_URL,
});

// Coordenadas por defecto de Santa Cruz de la Sierra (Plaza 24 de Septiembre)
const DEFAULT_LOC = { lat: "-17.7833", lng: "-63.1821", dept: 1 };

export const useAuthStore = create((set) => ({
  user: JSON.parse(localStorage.getItem('user')) || null,
  isAuthenticated: !!localStorage.getItem('token'),
  activeStoreId: localStorage.getItem('active_store_id') || null,
  activeStoreRole: localStorage.getItem('active_store_role') || null,
  myStores: [],
  loading: false,

  acceptSession: (data) => {
    if (!data?.token || !data?._id) throw new Error('La sesión recibida es inválida.');
    localStorage.removeItem('active_store_id');
    localStorage.removeItem('active_store_role');
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data));
    set({ user: data, isAuthenticated: true, activeStoreId: null, activeStoreRole: null, myStores: [], loading: false });
  },

  register: async (userData) => {
    set({ loading: true });
    try {
      // Combinamos los datos del form con los datos automáticos de SCZ
      const finalData = {
        ...userData,
        username: userData.email, // Usamos el email como username
        latitud: DEFAULT_LOC.lat,
        longitud: DEFAULT_LOC.lng,
        department: DEFAULT_LOC.dept,
        personal_code: `SCZ-${Math.floor(Math.random() * 10000)}` // Código autogenerado
      };

      const { data } = await api.post('/auth/register', finalData);
      if (data?.pendingVerification) {
        set({ loading: false });
        return { success: true, pendingVerification: true, message: data.message };
      }
      localStorage.removeItem('active_store_id');
      localStorage.removeItem('active_store_role');
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data));
      set({
        user: data,
        isAuthenticated: true,
        activeStoreId: null,
        activeStoreRole: null,
        myStores: [],
        loading: false,
      });
      return { success: true };
    } catch (error) {
      set({ loading: false });
      return { success: false, error: error.response?.data?.message || 'Error al crear cuenta' };
    }
  },

  login: async (username, password) => {
    set({ loading: true });
    try {
      const { data } = await api.post('/auth/login', { username, password });
      localStorage.removeItem('active_store_id');
      localStorage.removeItem('active_store_role');
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data));
      set({
        user: data,
        isAuthenticated: true,
        activeStoreId: null,
        activeStoreRole: null,
        myStores: [],
        loading: false,
      });
      return { success: true };
    } catch (error) {
      set({ loading: false });
      return { success: false, error: error.response?.data?.message || 'Credenciales inválidas' };
    }
  },

  refreshApps: async () => {
    try {
      // 👇 siempre partimos del localStorage
      const storedUser = JSON.parse(localStorage.getItem('user'));
      if (!storedUser) return;

      const { data } = await axiosInstance.get('/users/me/apps');

      const updatedUser = {
        ...storedUser,
        apps: data.apps
      };

      localStorage.setItem('user', JSON.stringify(updatedUser));

      set({ user: updatedUser });
    } catch (e) {
      console.error('Error refrescando apps', e);
    }
  },

  fetchMyStores: async () => {
    try {
      const { data } = await axiosInstance.get('/auth/me/stores');
      const stores = Array.isArray(data) ? data : [];
      set({ myStores: stores });
      return stores;
    } catch (error) {
      set({ myStores: [] });
      return [];
    }
  },

  selectActiveStore: (store) => {
    const storeId = String(store?.store_id || '').trim();
    const role = String(store?.role || '').trim().toUpperCase();
    if (!storeId) return false;

    localStorage.setItem('active_store_id', storeId);
    localStorage.setItem('active_store_role', role);
    set({ activeStoreId: storeId, activeStoreRole: role });
    return true;
  },

  clearActiveStore: () => {
    localStorage.removeItem('active_store_id');
    localStorage.removeItem('active_store_role');
    set({ activeStoreId: null, activeStoreRole: null });
  },


  logout: async () => {
    try {
        // 1. Respaldar todo en la nube antes de borrar
        // Esto asegura que los últimos cambios locales suban a MongoDB
        await cloudSync.pushAll();
        console.log("Respaldo completado con éxito antes del logout.");
    } catch (error) {
        console.error("Error al respaldar antes de salir:", error);
        // Opcional: Podrías detener el logout aquí si el respaldo es crítico
        // if (!confirm("No se pudo respaldar. ¿Salir de todos modos?")) return;
    }

    // 2. Limpiar sesión y caches generales.
    // Keep account-scoped transaction queues and recovery backups. A failed
    // upload followed by logout must not erase the user's only pending copy.
    Object.keys(localStorage).forEach(key => {
      if (!key.startsWith('app-records:')) localStorage.removeItem(key);
    });

    // 3. Resetear el estado global de Zustand
    set({ 
        user: null, 
        isAuthenticated: false,
      activeStoreId: null,
      activeStoreRole: null,
      myStores: [],
        productos: [],    // Limpia también los datos en memoria si es necesario
        movimientos: [] 
    });

    // 4. Redirigir o recargar (opcional pero recomendado para limpiar estados residuales)
    window.location.href = '/login'; 
    }
}));
